// @file Composable for whiteboard shape actions
import { awaitDOMUpdates } from '@@/bits/dom'
import { captureException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { useTldrawMenuClipboardEvents } from '@@/bits/tldraw/tldraw_hooks'
import { deleteShapeMetadata, getAllShapesInGroup, isGroupShape } from '@@/bits/whiteboard'
import { isValidAttachmentShape } from '@@/bits/whiteboard_shape_checks'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { SnackbarNotificationType } from '@@/enums'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardClipboardPosition } from '@@/vuecomposables/useWhiteboardClipboardPosition'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { TLDefaultTextAlignStyle, TLShape } from 'tldraw'

export function useWhiteboardShapeActions(): {
  copyShapes: (selectedShapes: TLShape[]) => Promise<boolean>
  pasteShapes: () => Promise<void>
  updateShapesTextAlignment: (shapes: TLShape[], align: TLDefaultTextAlignStyle) => boolean
} {
  const whiteboardStore = useWhiteboardStore()
  const surfacePostsStore = useSurfacePostsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const { setClipboardSource } = useWhiteboardClipboardPosition()

  const warnIfGroupContainsDisabledShapes = (shapes: TLShape[]): void => {
    const hasDisabledDescendant = shapes.some((shape) => {
      if (!isGroupShape(shape)) return false
      return getAllShapesInGroup(shape).some(
        (descendant) => !isGroupShape(descendant) && !whiteboardToolsStore.wasShapeCreatedFromAllowedTool(descendant),
      )
    })

    if (hasDisabledDescendant) {
      globalSnackbarStore.setSnackbar(
        {
          message: __("Some objects in this group are from disabled tools and won't appear when pasted"),
          notificationType: SnackbarNotificationType.warning,
        },
        { allowDuplicate: false },
      )
    }
  }

  /**
   * Copy attachment shapes with source metadata injected temporarily
   * This ensures the clipboard data includes the source information for proper deep copying on paste
   */
  const copyShapes = async (selectedShapes: TLShape[]): Promise<boolean> => {
    const { editor } = useTldrawEditor()

    if (editor == null) {
      return false
    }
    try {
      warnIfGroupContainsDisabledShapes(selectedShapes)

      const attachmentShapes = selectedShapes.filter((shape) => isValidAttachmentShape(shape))

      if (attachmentShapes.length === 0) {
        // No attachment shapes, perform regular copy
        await useTldrawMenuClipboardEvents().copy('unknown')
        return true
      }

      editor.run(() => {
        // First, inject the metadata
        for (const shape of attachmentShapes) {
          const postId = (shape as any).props.postId
          const post = surfacePostsStore.postEntitiesById?.[postId]

          // Inject metadata into the shape so it's included in clipboard data
          editor.updateShape({
            ...shape,
            meta: {
              ...shape.meta,
              sourceWishHashid: post?.hashid,
            },
          })
        }
      })

      // Perform the copy operation
      await useTldrawMenuClipboardEvents().copy('unknown')

      // Clean up the temporary metadata
      const shapeIds = attachmentShapes.map((shape) => shape.id)

      editor.run(() => {
        shapeIds.forEach((shapeId) => {
          deleteShapeMetadata(shapeId, ['sourceWishHashid', 'isBeingDeepCopied'])
        })
      })

      setClipboardSource()
      return true
    } catch (error) {
      captureException(error)
      return false
    }
  }

  /**
   * Handles pasting attachment shapes by performing deep copy operations
   * and updating the shapes with new post IDs
   */
  const pasteShapes = async (): Promise<void> => {
    const { editor } = useTldrawEditor()
    if (editor == null) return

    // We must await DOM updates to ensure the NEW shapes will be updated with the new post ids
    await awaitDOMUpdates()

    // Get currently selected shapes (should be the newly pasted ones)
    const pastedShapes = editor.getSelectedShapes()
    const attachmentShapes = pastedShapes.filter((shape) => isValidAttachmentShape(shape))

    if (attachmentShapes.length === 0) return

    // Mark shapes as being deep copied
    const shapesBeingCopied = attachmentShapes.map((shape) => ({
      ...shape,
      meta: {
        ...shape.meta,
        isBeingDeepCopied: true,
      },
    }))
    editor.run(() => {
      editor.updateShapes(shapesBeingCopied)
    })

    // Perform deep copy for these shapes using unified function
    const shapeIds = attachmentShapes.map((shape) => shape.id)
    const deepCopyResult = await whiteboardStore.deepCopyShapes(shapeIds)

    if (deepCopyResult !== false) {
      // Get the updated shapes (with isBeingDeepCopied flag) from the editor
      const currentShapes = shapeIds.map((id) => editor.getShape(id)).filter((shape): shape is TLShape => shape != null)

      // Update the pasted shapes with new post IDs (but keep isBeingDeepCopied temporarily)
      const updatedShapes = currentShapes.map((shape) => {
        const originalPostId = (shape as PadletAttachmentShape).props.postId
        const newPost = deepCopyResult[originalPostId]

        return {
          ...shape,
          props: {
            ...shape.props,
            postId: Number(newPost?.id) || originalPostId, // Fallback to original if new post creation failed
          },
        }
      })

      editor.run(() => {
        Object.values(deepCopyResult).forEach((post) => {
          surfacePostsStore.addPost(post)
        })

        editor.updateShapes(updatedShapes)

        shapeIds.forEach((shapeId) => {
          deleteShapeMetadata(shapeId, ['sourceWishHashid', 'isBeingDeepCopied'])
        })
      })
    } else {
      // Clean up the isBeingDeepCopied flag for failed deep copy
      editor.run(() => {
        shapeIds.forEach((shapeId) => {
          deleteShapeMetadata(shapeId, ['isBeingDeepCopied'])
        })
      })
    }
  }

  /**
   * Updates the text alignment for the given shapes.
   *
   * @param shapes - Array of shapes to update the text alignment for
   * @param align - The alignment to apply: 'start' (left), 'middle' (center), or 'end' (right)
   * @returns {boolean} True if shapes were actually updated, false if no text shapes found or operation failed
   *
   * @example
   * const { updateShapesTextAlignment } = useWhiteboardShapeActions()
   * const didUpdate = updateShapesTextAlignment(selectedShapes, 'middle')
   * if (didUpdate) {
   *   trackEvent('Whiteboard', 'Align text center')
   * }
   */
  const updateShapesTextAlignment = (shapes: TLShape[], align: TLDefaultTextAlignStyle): boolean => {
    const editor = useTldrawEditor().editor
    if (editor == null || shapes.length === 0) return false

    // Expand groups and filter to only text shapes
    // Do not align geo shapes as per https://linear.app/padlet/issue/PRO-22237
    const textShapes = shapes
      .flatMap((shape) => (isGroupShape(shape) ? getAllShapesInGroup(shape) : [shape]))
      .filter((shape) => !['geo', 'padlet-speech-bubble', 'padlet-ooxml-shape'].includes(shape.type))
      .filter((shape) => 'richText' in shape.props) // In a group with text, rich text, and non-rich text shapes, we want to align all of the ones that can be aligned

    if (textShapes.length === 0) return false

    editor.run(() => {
      const shapesToUpdate = textShapes.map((shape) => {
        const isTextShape = shape.type === 'text'

        if (isTextShape) {
          // For regular text shapes, update the textAlign prop
          return {
            ...shape,
            props: {
              ...shape.props,
              textAlign: align,
            },
          }
        } else {
          // For rich text shapes (geo, note, etc.), update the align prop
          return {
            ...shape,
            props: {
              ...shape.props,
              align,
            },
          }
        }
      })

      editor.updateShapes(shapesToUpdate)
    })

    return true
  }

  return {
    copyShapes,
    pasteShapes,
    updateShapesTextAlignment,
  }
}
